import { useCallback, useMemo } from 'react'
import { useTheme } from 'styled-components/native'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'

import i18n from 'APP/I18n'

import SearchResults from './SearchResults'
import { LoadingSkeleton, InitialPrompt, NoResults, SearchError } from './SearchStates'
import { MIN_SEARCH_TEXT_LENGTH } from './helpers'
import { DisplayResult } from './types'
import {
  StyledSafeAreaView,
  StyledView,
  StyledElementsWrapper,
  StyledInputWrapper,
  StyledInput,
  StyledClose,
  StyledSearchResults,
} from './styles'
import Analytics from 'APP/Services/Analytics'

export type Props = {
  searchText: string
  onChangeText: (text: string) => void
  onClose: () => void
  loading: boolean
  onRetry: () => void
  error: Error | null
  normalizedResults: DisplayResult[] | null
  onResultPress: (item: DisplayResult) => void
}

const ModalContent = ({
  searchText,
  onChangeText,
  onClose,
  loading,
  onRetry,
  error,
  normalizedResults,
  onResultPress,
}: Props) => {
  const theme = useTheme()

  const inputStyles = useMemo(
    () => ({
      inputContainer: { borderBottomWidth: 0 },
      input: { color: theme.colors.text, height: theme.metrics.icon.large },
    }),
    [theme.colors.text, theme.metrics.icon.large]
  )

  const hasQuery = searchText.length > MIN_SEARCH_TEXT_LENGTH

  let resultsContent: React.ReactNode = <InitialPrompt />

  const handleResultPress = useCallback(
    (item: DisplayResult) => {
      onClose()
      onResultPress(item)
    },
    [onResultPress, onClose]
  )

  if (hasQuery) {
    if (error) {
      resultsContent = <SearchError onRetry={onRetry} onClose={onClose} />
    } else if (loading) {
      resultsContent = <LoadingSkeleton />
    } else if (normalizedResults !== null && normalizedResults.length > 0) {
      resultsContent = <SearchResults data={normalizedResults} onResultPress={handleResultPress} />
    } else if (normalizedResults !== null && normalizedResults.length === 0) {
      resultsContent = <NoResults onClose={onClose} />
    }
  }

  return (
    <StyledSafeAreaView>
      <StyledView testID="SEMANTIC_SEARCH_MODAL">
        <StyledElementsWrapper>
          <StyledInputWrapper>
            <StyledInput
              inputContainerStyle={inputStyles.inputContainer}
              inputStyle={inputStyles.input}
              placeholderTextColor={theme.colors.secondaryText}
              placeholder={i18n.t('SemanticSearch.inputPlaceholder')}
              value={searchText}
              onChangeText={onChangeText}
              autoFocus={true}
              testID="SEMANTIC_SEARCH_INPUT"
            />
          </StyledInputWrapper>
          <StyledClose
            onPress={() => {
              Analytics.trackEvent('button_click', {
                button_value: 'Semantic Search Close',
              })
              onClose()
            }}
            testID="CLOSE_SEMANTIC_SEARCH"
          >
            <MaterialIcon name="close" size={theme.metrics.icon.small} color={theme.colors.text} />
          </StyledClose>
        </StyledElementsWrapper>
        <StyledSearchResults>{resultsContent}</StyledSearchResults>
      </StyledView>
    </StyledSafeAreaView>
  )
}

export default ModalContent
